import type {
  UpstreamKeyBucketRecord,
  CreateUpstreamKeyBucketRequest,
  CreateUpstreamKeyBucketResponse,
  DeleteUpstreamKeyBucketResponse,
  GetUpstreamKeyBucketSecretResponse,
  UpdateUpstreamKeyBucketRequest,
  UpdateUpstreamKeyBucketResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { invalidateRoutingTargetDisplayNames } from './routingTargetDisplayNames.ts';
import {
  createKeyBucket,
  deleteKeyBucket,
  getKeyBucketSecret,
  listKeyBuckets,
  updateKeyBucket,
} from './keyBuckets.ts';

const KB_KEY = ['key-buckets'] as const;

export function useKeyBucketsList(upstreamId: string): UseQueryResult<readonly UpstreamKeyBucketRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...KB_KEY, upstreamId],
    queryFn: async () => {
      const res = await listKeyBuckets(upstreamId, fetchImpl);
      return res.key_buckets;
    },
  });
}

export function useCreateKeyBucket(upstreamId: string): UseMutationResult<
  CreateUpstreamKeyBucketResponse,
  Error,
  CreateUpstreamKeyBucketRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUpstreamKeyBucketRequest) =>
      createKeyBucket(upstreamId, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [...KB_KEY, upstreamId] }); },
  });
}

export function useRevealKeyBucketSecret(upstreamId: string): UseMutationResult<
  GetUpstreamKeyBucketSecretResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (bucketId: string) => getKeyBucketSecret(upstreamId, bucketId, fetchImpl),
  });
}

export function useUpdateKeyBucket(upstreamId: string): UseMutationResult<
  UpdateUpstreamKeyBucketResponse,
  Error,
  { bucketId: string; body: UpdateUpstreamKeyBucketRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bucketId, body }) =>
      updateKeyBucket(upstreamId, bucketId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KB_KEY, upstreamId] });
      invalidateRoutingTargetDisplayNames(qc);
    },
  });
}

export function useDeleteKeyBucket(upstreamId: string): UseMutationResult<
  DeleteUpstreamKeyBucketResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bucketId: string) =>
      deleteKeyBucket(upstreamId, bucketId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KB_KEY, upstreamId] });
      invalidateRoutingTargetDisplayNames(qc);
    },
  });
}
